import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { AlertTriangle, Check, Info, X } from "lucide-react";

type Tone = "danger" | "warning" | "info";

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
}

interface AlertOptions {
  title: string;
  message: ReactNode;
  tone?: Tone;
  buttonLabel?: string;
}

interface DialogCtx {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
}

const Ctx = createContext<DialogCtx | null>(null);

interface DialogState {
  kind: "confirm" | "alert";
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  buttonLabel?: string;
  tone: Tone;
  resolve: (value: boolean) => void;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({
        kind: "confirm",
        title: opts.title,
        message: opts.message,
        confirmLabel: opts.confirmLabel ?? "Konfirmasi",
        cancelLabel: opts.cancelLabel ?? "Batal",
        tone: opts.tone ?? "danger",
        resolve,
      });
    });
  }, []);

  const alertFn = useCallback((opts: AlertOptions) => {
    return new Promise<void>((resolve) => {
      setState({
        kind: "alert",
        title: opts.title,
        message: opts.message,
        buttonLabel: opts.buttonLabel ?? "OK",
        tone: opts.tone ?? "info",
        resolve: () => resolve(),
      });
    });
  }, []);

  function close(value: boolean) {
    if (state) {
      state.resolve(value);
      setState(null);
    }
  }

  return (
    <Ctx.Provider value={{ confirm, alert: alertFn }}>
      {children}
      {state && <DialogView state={state} onClose={close} />}
    </Ctx.Provider>
  );
}

export function useDialog(): DialogCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
}

function DialogView({
  state,
  onClose,
}: {
  state: DialogState;
  onClose: (v: boolean) => void;
}) {
  const isConfirm = state.kind === "confirm";
  const tone = state.tone;

  const iconBg =
    tone === "danger"
      ? "bg-rose-50 border-rose-100"
      : tone === "warning"
        ? "bg-amber-50 border-amber-100"
        : "bg-brand-50 border-brand-100";
  const iconColor =
    tone === "danger"
      ? "text-rose-600"
      : tone === "warning"
        ? "text-amber-600"
        : "text-brand-700";
  const IconEl =
    tone === "danger" || tone === "warning" ? AlertTriangle : Info;

  const confirmBtnClass =
    tone === "danger"
      ? "bg-rose-600 hover:bg-rose-700 text-white"
      : tone === "warning"
        ? "bg-amber-600 hover:bg-amber-700 text-white"
        : "bg-brand-600 hover:bg-brand-700 text-white";

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white rounded-2xl border border-mailtera-border shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${iconBg}`}
          >
            {isConfirm ? (
              <IconEl size={18} className={iconColor} />
            ) : (
              <Check size={18} className={iconColor} />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-1">{state.title}</h3>
            <div className="text-sm text-slate-600">{state.message}</div>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-slate-400 hover:text-slate-700"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          {isConfirm ? (
            <>
              <button
                onClick={() => onClose(false)}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
              >
                {state.cancelLabel}
              </button>
              <button
                onClick={() => onClose(true)}
                className={`inline-flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-sm font-medium shadow-sm ${confirmBtnClass}`}
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </>
          ) : (
            <button
              onClick={() => onClose(true)}
              className="inline-flex items-center gap-1 bg-brand-600 hover:bg-brand-700 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium shadow-sm"
              autoFocus
            >
              {state.buttonLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
